import React, { FC, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import AgendaItemsTemplateInput from './AgendaItemsTemplateInput';
import TemplatesAdminPageBase, {
  TemplatesAdminPageProps,
} from './TemplatesAdminPageBase';
import { TemplateTypeConfigs } from './templateTypeConfigs';
import { AgendaTemplateContent, TEMPLATE_TYPES } from './types';

const getTypeConfigs = (
  formatMessage: IntlShape['formatMessage']
): TemplateTypeConfigs => ({
  [TEMPLATE_TYPES.ONE_ON_ONE_AGENDA]: {
    emptyFormValues: { agenda_items: [''], category: '' },
    fromContent: (content) => {
      const agenda = content as AgendaTemplateContent;
      return {
        agenda_items: agenda.items ?? [],
        category: agenda.category ?? '',
      };
    },
    inputs: [
      {
        name: 'agenda_items',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: AgendaItemsTemplateInput,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.form.agenda_items',
          defaultMessage: 'Agenda items',
        }),
      },
      {
        name: 'category',
        type: INPUT_TYPES.TEXT,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.form.category',
          defaultMessage: 'Category',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.templates.form.category_helper',
          defaultMessage:
            'Optional. When every 1:1 agenda template has a category, templates are grouped by it in the "Add from template" menu.',
        }),
        maxLength: 100,
      },
    ],
    validate: (obj) => {
      const errors: Record<string, string> = {};
      const items = ((obj.agenda_items as string[]) ?? []).filter((i) =>
        i?.trim()
      );
      if (items.length === 0) {
        errors.agenda_items = formatMessage({
          id: 'app.views.administration.content_administration.templates.form.error.agenda_items_required',
          defaultMessage: 'Add at least one agenda item.',
        });
      }
      return errors;
    },
    toContent: (obj) => ({
      items: ((obj.agenda_items as string[]) ?? []).filter((i) => i?.trim()),
      category: (obj.category as string | undefined)?.trim() || null,
    }),
    describeContent: (template) =>
      formatMessage(
        {
          id: 'app.views.administration.content_administration.templates.row.agenda_items_count',
          defaultMessage:
            '{count, plural, one {# agenda item} other {# agenda items}}',
        },
        {
          count: (template.content as AgendaTemplateContent).items?.length ?? 0,
        }
      ),
  },
});

const OneOnOneAgendaTemplatesPage: FC<TemplatesAdminPageProps> = (props) => {
  const { formatMessage } = useIntl();
  const typeConfigs = useMemo(
    () => getTypeConfigs(formatMessage),
    [formatMessage]
  );
  return (
    <TemplatesAdminPageBase
      templateType={TEMPLATE_TYPES.ONE_ON_ONE_AGENDA}
      typeConfigs={typeConfigs}
      {...props}
    />
  );
};

export default React.memo(OneOnOneAgendaTemplatesPage);
